'use client';

import { useEffect, useMemo, useState } from 'react';

import dynamic from 'next/dynamic';

import { ThemeProvider } from 'next-themes';

import { I18nProvider } from '@kit/i18n/provider';
import { MonitoringProvider } from '@kit/monitoring/components';
import { AppEventsProvider } from '@kit/shared/events';
import { useUser } from '@kit/supabase/hooks/use-user';
import { If } from '@kit/ui/if';
import { VersionUpdater } from '@kit/ui/version-updater';

import { AnalyticsProvider } from '~/components/analytics-provider';
import { PostHogProvider } from '~/components/analytics/posthog-provider';
import { AuthProvider } from '~/components/auth-provider';
// Direct module imports, NOT the '~/components/auth' barrel: the barrel
// re-exports AuthModal (framer-motion + the full auth form stack), and a
// barrel import here would drag all of it back into the sitewide chunk,
// defeating the dynamic() split below.
import {
  AuthModalProvider,
  useAuthModal,
} from '~/components/auth/auth-modal-context';
import { SessionExpiredWatcher } from '~/components/auth/session-expired-watcher';
import { CharacterQuickViewProvider } from '~/components/characters/quick-view/quick-view-provider';
import { CrashReporter } from '~/components/crash-reporter';
import { CurrencyProvider } from '~/components/currency-provider';
import { LanguageSync } from '~/components/language-sync';
import {
  BuyCoinsModalProvider,
  useBuyCoinsModal,
} from '~/components/wallet/buy-coins-modal-context';
import { useInvalidateCoinBalance } from '~/components/wallet/coin-balance';
import {
  type CoinBalanceData,
  CoinBalanceProvider,
} from '~/components/wallet/coin-balance-provider';
import { InsufficientCoinsWatcher } from '~/components/wallet/insufficient-coins-watcher';
import {
  OutOfCoinsProvider,
  useOutOfCoins,
} from '~/components/wallet/out-of-coins-context';
import appConfig from '~/config/app.config';
import featuresFlagConfig from '~/config/feature-flags.config';
import { COIN_PURCHASES_ENABLED } from '~/config/monetization.config';
import { type DisplayCurrency } from '~/lib/currency';
import { i18nResolver } from '~/lib/i18n/i18n.resolver';
import { getI18nSettings } from '~/lib/i18n/i18n.settings';

import { ReactQueryProvider } from './react-query-provider';

// The always-mounted overlays (auth modal, coin dialogs, login reward) pulled
// framer-motion, Radix Dialog and the whole checkout stack into the sitewide
// chunk while rendering nothing for most visitors. Each is code-split (same
// pattern as StoryViewer in stories-row.tsx) and mounted only when its
// trigger state first flips — the chunk loads on demand, not at hydration.
const AuthModalLazy = dynamic(() =>
  import('~/components/auth/auth-modal').then((m) => m.AuthModal),
);
const OutOfCoinsDialogLazy = dynamic(() =>
  import('~/components/wallet/out-of-coins-dialog').then(
    (m) => m.OutOfCoinsDialog,
  ),
);
const BuyCoinsModalLazy = dynamic(() =>
  import('~/components/wallet/buy-coins-modal').then((m) => m.BuyCoinsModal),
);
const DailyLoginRewardLazy = dynamic(() =>
  import('~/components/engagement/daily-login-reward').then(
    (m) => m.DailyLoginReward,
  ),
);

/** True once `value` has ever been true — keeps a modal mounted after its
 * first open so the component's own open/close animation keeps working. */
function useStickyTrue(value: boolean) {
  const [ever, setEver] = useState(false);
  useEffect(() => {
    if (value) setEver(true);
  }, [value]);
  return ever || value;
}

function LazyAuthModal() {
  const { isOpen } = useAuthModal();
  return useStickyTrue(isOpen) ? <AuthModalLazy /> : null;
}

function LazyOutOfCoinsDialog() {
  const { isOpen } = useOutOfCoins();
  return useStickyTrue(isOpen) ? <OutOfCoinsDialogLazy /> : null;
}

function LazyDailyLoginReward() {
  // Logged-out visitors (all marketing traffic) can never see the reward —
  // don't even load its module for them.
  const { data: user } = useUser();
  return user ? <DailyLoginRewardLazy /> : null;
}

function GlobalBuyCoinsModal() {
  const { isOpen, closeBuyCoinsModal } = useBuyCoinsModal();
  const { openOutOfCoins } = useOutOfCoins();
  const invalidate = useInvalidateCoinBalance();

  // Coin packs hidden: any "buy coins" trigger anywhere in the app becomes
  // the subscription upsell instead. Call sites stay untouched, so flipping
  // COIN_PURCHASES_ENABLED back restores the pack modal everywhere at once.
  useEffect(() => {
    if (!COIN_PURCHASES_ENABLED && isOpen) {
      closeBuyCoinsModal();
      openOutOfCoins();
    }
  }, [isOpen, closeBuyCoinsModal, openOutOfCoins]);

  const mount = useStickyTrue(COIN_PURCHASES_ENABLED && isOpen);

  if (!COIN_PURCHASES_ENABLED || !mount) return null;

  return (
    <BuyCoinsModalLazy
      open={isOpen}
      onOpenChange={(v) => {
        if (!v) closeBuyCoinsModal();
      }}
      onPurchaseComplete={invalidate}
    />
  );
}

type RootProvidersProps = React.PropsWithChildren<{
  // The language to use for the app (optional)
  lang?: string;
  // The theme (light or dark or system) (optional)
  theme?: string;
  // The CSP nonce to pass to scripts (optional)
  nonce?: string;
  // Initial coin balance from server (null for unauthenticated users)
  initialCoinBalance?: CoinBalanceData | null;
  // Region-resolved display currency (server reads x-vercel-ip-country)
  currency?: DisplayCurrency;
}>;

export function RootProviders({
  lang,
  theme = appConfig.theme,
  nonce,
  initialCoinBalance,
  currency = 'USD',
  children,
}: RootProvidersProps) {
  const i18nSettings = useMemo(() => getI18nSettings(lang), [lang]);

  return (
    <CurrencyProvider currency={currency}>
      <MonitoringProvider>
        <AppEventsProvider>
          <AnalyticsProvider>
            <PostHogProvider>
              <ReactQueryProvider>
                {/* CoinBalanceProvider at ROOT - INSIDE ReactQueryProvider */}
                <CoinBalanceProvider
                  initialBalance={initialCoinBalance ?? undefined}
                >
                  <I18nProvider settings={i18nSettings} resolver={i18nResolver}>
                    {/* Static pages ship the default language; this corrects
                        from the lang cookie after hydration. */}
                    <LanguageSync />
                    <AuthProvider>
                      <ThemeProvider
                        attribute="class"
                        enableSystem
                        disableTransitionOnChange
                        defaultTheme={theme}
                        // Day mode is parked until every app page has a
                        // light design (half-light pages looked broken). The
                        // palette and toggles stay in the code; removing this
                        // line turns it back on.
                        forcedTheme="dark"
                        enableColorScheme={false}
                        nonce={nonce}
                      >
                        <AuthModalProvider>
                          <BuyCoinsModalProvider>
                            <OutOfCoinsProvider>
                              <CrashReporter>
                                <CharacterQuickViewProvider>
                                  {children}
                                </CharacterQuickViewProvider>
                              </CrashReporter>
                              <LazyAuthModal />
                              <SessionExpiredWatcher />
                              <InsufficientCoinsWatcher />
                              <GlobalBuyCoinsModal />
                              <LazyOutOfCoinsDialog />
                              <LazyDailyLoginReward />
                            </OutOfCoinsProvider>
                          </BuyCoinsModalProvider>
                        </AuthModalProvider>
                      </ThemeProvider>
                    </AuthProvider>

                    <If condition={featuresFlagConfig.enableVersionUpdater}>
                      <VersionUpdater />
                    </If>
                  </I18nProvider>
                </CoinBalanceProvider>
              </ReactQueryProvider>
            </PostHogProvider>
          </AnalyticsProvider>
        </AppEventsProvider>
      </MonitoringProvider>
    </CurrencyProvider>
  );
}
